import { useState, useEffect } from 'react'
import { ref, onValue } from 'firebase/database'
import { firebase_realtime } from '../../../common/firebaseConfig'
import murmurhash from 'murmurhash'
import { Pattern } from '../../builder/contexts/PatternContext'

interface StatusInfo {
  buyStatusCount: number
  buyTotalCount: number
  sellStatusCount: number
  sellTotalCount: number
  isLoading: boolean
  buyPercentage: number
  sellPercentage: number
  buyStatuses: { [key: string]: boolean }
  sellStatuses: { [key: string]: boolean }
}

// Helper function to sort object keys for consistent hashing
const sortObjectKeys = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(sortObjectKeys)

  return Object.keys(obj)
    .sort()
    .reduce((result: any, key) => {
      result[key] = sortObjectKeys(obj[key])
      return result
    }, {})
}

// Helper function to flatten condition groups
const getAllConditionsFromGroup = (group: any, groupTitle: string = '') => {
  let allConditions: any[] = []

  if (group.conditions) {
    for (const condition of group.conditions) {
      if (groupTitle && groupTitle.trim() !== '') {
        condition.groupTitle = groupTitle
      }
      // Add group information to each condition
      condition.groupId = group.id
      condition.requiredCount = group.count || group.conditions.length // Default to all conditions if count is not specified
      condition.totalInGroup = group.conditions.length
      allConditions.push(condition)
    }
  }

  if (group.conditionGroups) {
    for (const conditionGroup of group.conditionGroups) {
      let title = groupTitle
      if (conditionGroup.groupTitle && conditionGroup.groupTitle.trim() !== '') {
        title += `${conditionGroup.groupTitle}GC-GROUP-ID${group.id}GC-GROUP-ID.`
      }
      allConditions = [...allConditions, ...getAllConditionsFromGroup(conditionGroup, title)]
    }
  }

  return allConditions
}

// Helper function to flatten pattern
const flattenPattern = (pattern: Pattern, type: 'buy' | 'sell') => {
  let allConditions: any[] = []

  const conditionGroup = type === 'buy' ? pattern.buyConditionGroups : pattern.sellConditionGroups

  if (!conditionGroup) return []

  for (const group of conditionGroup) {
    allConditions = [
      ...allConditions,
      ...getAllConditionsFromGroup(group, `${group.groupTitle}GC-GROUP-ID${group.id}GC-GROUP-ID.`),
    ]
  }

  return allConditions
}

export const useAlertStatus = (pattern: Pattern | undefined, isActive: boolean): StatusInfo => {
  const [buyStatuses, setBuyStatuses] = useState<{ [key: string]: boolean }>({})
  const [sellStatuses, setSellStatuses] = useState<{ [key: string]: boolean }>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [buyConditionsMap, setBuyConditionsMap] = useState<{ [hash: string]: any }>({})
  const [sellConditionsMap, setSellConditionsMap] = useState<{ [hash: string]: any }>({})

  useEffect(() => {
    if (!pattern || !isActive) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const buyConditionListeners: (() => void)[] = []
    const sellConditionListeners: (() => void)[] = []

    // Process buy conditions
    const buyConditions = flattenPattern(pattern, 'buy')
    const newBuyConditionsMap: { [hash: string]: any } = {}

    // If there are no conditions, we're not loading
    if (buyConditions.length === 0 && flattenPattern(pattern, 'sell').length === 0) {
      setIsLoading(false)
    }

    buyConditions.forEach((condition) => {
      try {
        // Clean up condition for hashing
        if (condition.condition?.LHS?.indicator && !condition.condition.LHS.indicator.params) {
          delete condition.condition.LHS.indicator.params
        }
        if (condition.condition?.RHS?.indicator && !condition.condition.RHS.indicator.params) {
          delete condition.condition.RHS.indicator.params
        }

        // Create hashable condition
        const hashableCondition = {
          chartDetails: condition.chartDetails,
          settings: condition.settings,
          condition: condition.condition,
        }

        // Generate hash
        const conditionHash = murmurhash
          .v3(JSON.stringify(sortObjectKeys(hashableCondition)))
          .toString()

        // Store condition metadata with the hash
        newBuyConditionsMap[conditionHash] = {
          groupId: condition.groupId,
          requiredCount: condition.requiredCount,
          totalInGroup: condition.totalInGroup,
        }

        // Set up listener
        const statusRef = ref(firebase_realtime, `conditionStatus/${conditionHash}`)
        const unsubscribe = onValue(
          statusRef,
          (snapshot) => {
            if (snapshot.exists()) {
              setBuyStatuses((prev) => ({
                ...prev,
                [conditionHash]: snapshot.val(),
              }))
            }
            setIsLoading(false)
          },
          (error) => {
            console.error('Error fetching buy condition status:', error)
            setIsLoading(false)
          }
        )

        buyConditionListeners.push(unsubscribe)
      } catch (error) {
        console.error('Error setting up buy condition listener:', error)
        setIsLoading(false)
      }
    })

    setBuyConditionsMap(newBuyConditionsMap)

    // Process sell conditions
    const sellConditions = flattenPattern(pattern, 'sell')
    const newSellConditionsMap: { [hash: string]: any } = {}

    sellConditions.forEach((condition) => {
      try {
        // Clean up condition for hashing
        if (condition.condition?.LHS?.indicator && !condition.condition.LHS.indicator.params) {
          delete condition.condition.LHS.indicator.params
        }
        if (condition.condition?.RHS?.indicator && !condition.condition.RHS.indicator.params) {
          delete condition.condition.RHS.indicator.params
        }

        // Create hashable condition
        const hashableCondition = {
          chartDetails: condition.chartDetails,
          settings: condition.settings,
          condition: condition.condition,
        }

        // Generate hash
        const conditionHash = murmurhash
          .v3(JSON.stringify(sortObjectKeys(hashableCondition)))
          .toString()

        // Store condition metadata with the hash
        newSellConditionsMap[conditionHash] = {
          groupId: condition.groupId,
          requiredCount: condition.requiredCount,
          totalInGroup: condition.totalInGroup,
        }

        // Set up listener
        const statusRef = ref(firebase_realtime, `conditionStatus/${conditionHash}`)
        const unsubscribe = onValue(
          statusRef,
          (snapshot) => {
            if (snapshot.exists()) {
              setSellStatuses((prev) => ({
                ...prev,
                [conditionHash]: snapshot.val(),
              }))
            }
            setIsLoading(false)
          },
          (error) => {
            console.error('Error fetching sell condition status:', error)
            setIsLoading(false)
          }
        )

        sellConditionListeners.push(unsubscribe)
      } catch (error) {
        console.error('Error setting up sell condition listener:', error)
        setIsLoading(false)
      }
    })

    setSellConditionsMap(newSellConditionsMap)

    // If no conditions were processed, we're not loading
    if (buyConditions.length === 0 && sellConditions.length === 0) {
      setIsLoading(false)
    }

    // Cleanup function
    return () => {
      buyConditionListeners.forEach((unsubscribe) => unsubscribe())
      sellConditionListeners.forEach((unsubscribe) => unsubscribe())
    }
  }, [pattern, isActive])

  // Calculate status counts with the new logic
  const calculateGroupPercentages = (
    statuses: { [key: string]: boolean },
    conditionsMap: { [hash: string]: any }
  ) => {
    // Group conditions by their group ID
    const groups: {
      [groupId: string]: {
        metCount: number
        requiredCount: number
        totalCount: number
        conditions: string[]
      }
    } = {}

    // Initialize groups
    Object.entries(conditionsMap).forEach(([hash, info]) => {
      const { groupId, requiredCount, totalInGroup } = info

      if (!groups[groupId]) {
        groups[groupId] = {
          metCount: 0,
          requiredCount: requiredCount,
          totalCount: totalInGroup,
          conditions: [],
        }
      }

      groups[groupId].conditions.push(hash)

      // Count met conditions
      if (statuses[hash]) {
        groups[groupId].metCount++
      }
    })

    let totalRequired = 0
    let totalMet = 0

    // Calculate percentages per group
    Object.values(groups).forEach((group) => {
      // Add the required count to the total required
      totalRequired += group.requiredCount

      // If the group is fully met (has required number of conditions), count all required conditions as met
      if (group.metCount >= group.requiredCount) {
        totalMet += group.requiredCount
      } else {
        // Otherwise, count the actual met conditions
        totalMet += group.metCount
      }
    })

    return {
      metCount: totalMet,
      requiredCount: totalRequired,
    }
  }

  // Calculate buy percentages
  const buyCalculation = calculateGroupPercentages(buyStatuses, buyConditionsMap)
  const buyStatusCount = buyCalculation.metCount
  const buyTotalCount = buyCalculation.requiredCount
  const buyPercentage = buyTotalCount > 0 ? (buyStatusCount / buyTotalCount) * 100 : 0

  // Calculate sell percentages
  const sellCalculation = calculateGroupPercentages(sellStatuses, sellConditionsMap)
  const sellStatusCount = sellCalculation.metCount
  const sellTotalCount = sellCalculation.requiredCount
  const sellPercentage = sellTotalCount > 0 ? (sellStatusCount / sellTotalCount) * 100 : 0

  return {
    buyStatusCount,
    buyTotalCount,
    sellStatusCount,
    sellTotalCount,
    isLoading,
    buyPercentage,
    sellPercentage,
    buyStatuses,
    sellStatuses,
  }
}
